import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  ThemeProvider,
  Snackbar,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import axios from "axios";
import { SERVER } from "../../../../Constants";
import ReportSearchBar from "./ReportSearchbar";
import ReportList from "./ReportList";
import ReportActionDialog from "./ReportActionDialog";
import darkTheme from "./reportTheme";
import { sendMessage } from "../../../context/SystemMessage";
import { disableUser } from "../../../context/User";

const api = axios.create({ baseURL: SERVER, withCredentials: true });

export default function ReportComponent() {
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState("WARN");
  const [responseMessage, setResponseMessage] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await api.get("/report");
      setReports(res.data.reports || []);
    } catch {
      setError("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleDenyReport = async (id) => {
    await api.post("/report/action", {
      reportId: id,
      response: "INVALID",
      responseMsg: "Report dismissed",
    });
    fetchReports();
  };

  const handleSubmitAction = async () => {
    try {
      setLoading(true);
      
      // Generate default response message if not provided
      const getDefaultResponseMsg = () => {
        if (responseMessage && responseMessage.trim()) {
          return responseMessage;
        }
        
        switch (actionType) {
          case "WARN":
            return "Your content has been reported and reviewed. This is a warning for violating our Terms of Service. Please review our community guidelines to avoid further action.";
          case "DISABLE":
            return "Your account has been temporarily disabled due to Terms of Service violations. Please contact support for more information.";
          case "BAN":
            return "Your account has been permanently banned due to serious or repeated violations of our Terms of Service.";
          default:
            return "Action taken on your account due to reported content.";
        }
      };

      const finalResponseMsg = getDefaultResponseMsg();

      // Submit the report action
      const res = await api.post("/report/action", {
        reportId: selectedReport._id,
        response: "VALID",
        action: actionType,
        responseMsg: finalResponseMsg,
      });

      // Handle WARN action - send system message
      if (actionType === "WARN" && selectedReport.reportedUser?._id) {
        try {
          // Construct message with reported content details
          let warningMessage = finalResponseMsg;
          
          // Add reported content information
          if (selectedReport.messageText) {
            warningMessage += `\n\nReported Content: "${selectedReport.messageText}"`;
          } else if (selectedReport.details) {
            warningMessage += `\n\nViolation Details: ${selectedReport.details}`;
          }
          
          await sendMessage({
            userId: selectedReport.reportedUser._id,
            customMessage: {
              type: "warning",
              category: "content_violation",
              severity: "high",
              title: "Terms of Service Violation Warning",
              message: warningMessage,
            }
          });
          setSnackbar({ open: true, message: "Warning sent to user successfully", severity: "success" });
        } catch (err) {
          console.error("Failed to send warning message:", err);
          setSnackbar({ open: true, message: "Action recorded but warning message failed to send", severity: "warning" });
        }
      }

      // Handle DISABLE/BAN action
      if ((actionType === "DISABLE" || actionType === "BAN") && selectedReport.reportedUser?._id) {
        try {
          await disableUser(selectedReport.reportedUser._id);
          setSnackbar({ open: true, message: "User account disabled successfully", severity: "success" });
        } catch (err) {
          console.error("Failed to disable user:", err);
          setSnackbar({ open: true, message: "Action recorded but failed to disable user account", severity: "warning" });
        }
      }

      // Handle MESSAGE type - delete the message
      if (selectedReport.type === "MESSAGE") {
        try {
          await api.post("/report/deleteMessage", {
            reportId: selectedReport._id,
          });
          setSnackbar({ open: true, message: "Message deleted successfully", severity: "success" });
        } catch (err) {
          console.error("Failed to delete message:", err);
          setSnackbar({ open: true, message: "Action recorded but message deletion failed", severity: "warning" });
        }
      }

      // Send thank you message to the reporter
      if (selectedReport.reportedBy?._id) {
        try {
          await sendMessage({
            userId: selectedReport.reportedBy._id,
            customMessage: {
              type: "info",
              category: "general",
              severity: "low",
              title: "Thank You for Keeping MJMarry Safe",
              message: "Thank you for reporting content that violated our Terms of Service. Your report has been reviewed and appropriate action has been taken. We appreciate your help in making MJMarry a better and safer community for everyone.\n\n- The MJMarry Team",
            }
          });
        } catch (err) {
          console.error("Failed to send thank you message to reporter:", err);
          // Silent fail - don't show error to admin as this is a nice-to-have
        }
      }

      setActionDialogOpen(false);
      setActionType("WARN");
      setResponseMessage("");
      fetchReports();
    } catch (err) {
      console.error("Failed to submit action:", err);
      setSnackbar({ open: true, message: "Failed to process report action", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const filtered = reports.filter((r) => {
    const matchesSearch = [r.reportedUser?.fullname, r.reportedBy?.fullname, r.details, r.messageText, r._id]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase());
    
    const matchesType = !typeFilter || r.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  return (
    <ThemeProvider theme={darkTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "background.default",
          py: 5,
        }}
      >
        <Container maxWidth="xl">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {" "}
            {/* Header */}
            <Typography
              variant="h4"
              fontWeight="bold"
              mb={3}
              letterSpacing={0.3}
            >
              Report Management
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchReports}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          <ReportSearchBar 
            search={search} 
            onChange={setSearch}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
          />
          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Box textAlign="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <ReportList
              reports={filtered}
              search={search}
              onDeny={handleDenyReport}
              onAction={(r) => {
                setSelectedReport(r);
                setActionType("WARN");
                setResponseMessage("");
                setActionDialogOpen(true);
              }}
            />
          )}

          <ReportActionDialog
            open={actionDialogOpen}
            onClose={() => {
              setActionDialogOpen(false);
              setActionType("WARN");
              setResponseMessage("");
            }}
            onSubmit={handleSubmitAction}
            selectedReport={selectedReport}
            actionType={actionType}
            setActionType={setActionType}
            responseMessage={responseMessage}
            setResponseMessage={setResponseMessage}
          />

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
